/**
 * Builds the deep-link to a published question's detail page (the V1
 * `QuestionDetailsPage`) from an AuditWhiz review question + the author's
 * program.
 *
 * The V1 detail page loads a question only when ALL of `questionId`,
 * `programId`, `division`, and `businessUnit` are present in the query string
 * (see `QuestionDetailsPage`'s `useEffect` guard); a missing one leaves the
 * page unable to fetch. On a *published* AuditWhiz review question the first
 * three are structurally guaranteed — `questionId` is stamped at publish,
 * `divisionName` is required to publish, and `businessUnit` is stamped on every
 * generated draft — so `programId` (sourced separately from the logged-in user)
 * is the only value that can be absent. This returns `undefined` in that case
 * so the caller renders plain text instead of a dead link.
 *
 * Mirrors the query-param shape of the V1 `getQuestionDetailsUrl` helper
 * (`programId`, `questionId`, `division`, `businessUnit`) so the detail page
 * reads them unchanged.
 */
import routePaths from 'src/contstant/routePaths';
import type { ReviewQuestion } from './reviewTypes';

export interface PublishedQuestionHrefInput {
  /** The published review question (must carry a `questionId`). */
  readonly question: ReviewQuestion;
  /** The author's program id (from the logged-in user). The one value not
   *  carried on the question; absent → no link. */
  readonly programId: string | undefined;
}

/**
 * The detail-page URL for a published question, or `undefined` when it cannot
 * be built (no `programId`, or the question has no `questionId` yet — i.e. it
 * is not actually published). The caller treats `undefined` as "show the id as
 * plain text, not a link".
 */
export function buildPublishedQuestionHref({
  question,
  programId,
}: PublishedQuestionHrefInput): string | undefined {
  const { questionId, configuration } = question;
  if (!questionId || !programId) return undefined;

  const params = new URLSearchParams({
    programId,
    questionId,
    division: configuration.divisionName,
    businessUnit: configuration.businessUnit,
  });
  return `${routePaths.QUESTION_DETAILS.path}?${params.toString()}`;
}
